import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import PrivateRoute from './components/common/PrivateRoute';
import GuestRoute from './components/common/GuestRoute';
import AppShell from './components/layout/AppShell';

import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import WorkoutListPage from './pages/WorkoutListPage';
import WorkoutBuilderPage from './pages/WorkoutBuilderPage';
import ExerciseListPage from './pages/ExerciseListPage';
import ExerciseFormPage from './pages/ExerciseFormPage';
import ExerciseDetailPage from './pages/ExerciseDetailPage';
import PresenterListPage from './pages/PresenterListPage';
import FreeWorkoutListPage from './pages/FreeWorkoutListPage';
import PresenterPage from './pages/PresenterPage';
import NotFoundPage from './pages/NotFoundPage';
import MediaLibraryPage from './pages/MediaLibraryPage';
import AiConfigPage from './pages/settings/AiConfigPage';
import SoundSettingsPage from './pages/settings/SoundSettingsPage';
import GeneralSettingsPage from './pages/settings/GeneralSettingsPage';
import StopwatchPage from './pages/StopwatchPage';
import TimerPage from './pages/TimerPage';
import QuickStartPlaceholderPage from './pages/QuickStartPlaceholderPage';
import HIITPage from './pages/HIITPage';
import KampfsportPage from './pages/KampfsportPage';
import PyrPage from './pages/PyrPage';
import TimerPresetPage from './pages/TimerPresetPage';
import ProgressPage from './pages/ProgressPage';

function DefaultRedirect() {
  // Eingeloggte Nutzer → Bibliothek; Gäste (Gratis-Tier) → Umsonst-Workouts
  const { user } = useAuth();
  return <Navigate to={user ? '/workouts' : '/free'} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route
            path="/"
            element={
              <GuestRoute>
                <DefaultRedirect />
              </GuestRoute>
            }
          />
          <Route
            path="/free"
            element={
              <GuestRoute>
                <AppShell><FreeWorkoutListPage /></AppShell>
              </GuestRoute>
            }
          />
          <Route
            path="/workouts"
            element={
              <PrivateRoute roles={['admin', 'trainer', 'participant', 'super_admin']}>
                <AppShell><WorkoutListPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/workouts/new"
            element={
              <PrivateRoute roles={['admin', 'trainer', 'super_admin']}>
                <AppShell><WorkoutBuilderPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/workouts/:id/edit"
            element={
              <PrivateRoute roles={['admin', 'trainer', 'super_admin']}>
                <AppShell><WorkoutBuilderPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/exercises"
            element={
              <PrivateRoute roles={['admin', 'trainer', 'participant', 'super_admin']}>
                <AppShell><ExerciseListPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/exercises/new"
            element={
              <PrivateRoute roles={['admin', 'trainer', 'super_admin']}>
                <AppShell><ExerciseFormPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/exercises/:id/edit"
            element={
              <PrivateRoute roles={['admin', 'trainer', 'super_admin']}>
                <AppShell><ExerciseFormPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/exercises/:id"
            element={
              <PrivateRoute roles={['admin', 'trainer', 'participant', 'super_admin']}>
                <AppShell><ExerciseDetailPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/presenter"
            element={
              <PrivateRoute roles={['admin', 'trainer', 'participant']}>
                <AppShell><PresenterListPage /></AppShell>
              </PrivateRoute>
            }
          />
          {/* Gratis-Tier: Presenter für Umsonst-Workouts + alle Quick Starts/Tools – auch ohne Login */}
          <Route
            path="/presenter/:id"
            element={
              <GuestRoute>
                <PresenterPage />
              </GuestRoute>
            }
          />
          <Route
            path="/stopwatch"
            element={
              <GuestRoute>
                <AppShell><StopwatchPage /></AppShell>
              </GuestRoute>
            }
          />
          <Route
            path="/timer"
            element={
              <GuestRoute>
                <AppShell><TimerPage /></AppShell>
              </GuestRoute>
            }
          />
          <Route
            path="/quick-starts/hiit"
            element={
              <GuestRoute>
                <AppShell><HIITPage /></AppShell>
              </GuestRoute>
            }
          />
          <Route
            path="/quick-starts/pyramide"
            element={
              <GuestRoute>
                <AppShell><PyrPage /></AppShell>
              </GuestRoute>
            }
          />
          <Route
            path="/quick-starts/kampfsport"
            element={
              <GuestRoute>
                <AppShell><KampfsportPage /></AppShell>
              </GuestRoute>
            }
          />
          <Route
            path="/quick-starts/aufwaermen"
            element={
              <GuestRoute>
                <AppShell>
                  <TimerPresetPage
                    title="Aufwärmen"
                    icon="bi-thermometer-half"
                    accentColor="#f97316"
                    presets={[5, 10, 15, 20]}
                  />
                </AppShell>
              </GuestRoute>
            }
          />
          <Route
            path="/quick-starts/stretching"
            element={
              <GuestRoute>
                <AppShell>
                  <TimerPresetPage
                    title="Stretching"
                    icon="bi-person-arms-up"
                    accentColor="#06b6d4"
                    presets={[5, 10, 15, 20]}
                  />
                </AppShell>
              </GuestRoute>
            }
          />
          <Route
            path="/progress"
            element={
              <PrivateRoute roles={['admin', 'trainer', 'participant']}>
                <AppShell><ProgressPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/media"
            element={
              <PrivateRoute roles={['admin', 'trainer']}>
                <AppShell><MediaLibraryPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route path="/settings" element={<Navigate to="/settings/ai" replace />} />
          <Route
            path="/settings/ai"
            element={
              <PrivateRoute roles={['admin']}>
                <AppShell><AiConfigPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/sound"
            element={
              <PrivateRoute roles={['admin']}>
                <AppShell><SoundSettingsPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/general"
            element={
              <PrivateRoute roles={['admin']}>
                <AppShell><GeneralSettingsPage /></AppShell>
              </PrivateRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
